import React from 'react';
import { Card, NavigationItem } from '@/components';
import { TextTypeIcon, VideoTypeIcon } from '@/components/Icons';
import { format, R } from '@/features/booking/resources/Texts.resx';
import { Utils } from '@/shared/Utils';
import { useUrlState } from '@/features/booking/shared/model/useUrlState';
import { parseConsultationType } from '@/features/booking/shared/model/useBooking';
import { InsuranceProviderInfoCard } from './InsuranceProviderInfoCard';
import { ProductDto } from '@/shared/interfaces';

type SelectProductProps = {
    caregiverTitle: string;
    products: ProductDto[];
    shouldHidePrice: boolean;
    onSelectProduct: (product: ProductDto) => void;
    insuranceProviderName?: string;
    insuranceProviderLogoPath?: string;
};

export const SelectProduct: React.FC<SelectProductProps> = ({
    caregiverTitle,
    products,
    onSelectProduct,
    shouldHidePrice,
    insuranceProviderName,
    insuranceProviderLogoPath,
}) => {
    const getPriceFormatted = (product: ProductDto) => {
        if (shouldHidePrice) return '';

        return product.Price.Price == 0
            ? R.Free
            : `${Utils.formatPrice(product.Price.Price)} ${R.PerConsultation}`;
    };

    const videoProducts = products.filter((p) => p.IsVideo);
    const nonVideoProducts = products.filter((p) => !p.IsVideo);
    const shouldShowMissingCoverageInfo = products.every(
        (p) => p.Price.Price > 0 && !p.NeverShowMissingCoverageWarning,
    );

    const { urlState } = useUrlState();

    const isSelected = (product: ProductDto) => {
        return (
            product.ConsultationType ===
            parseConsultationType(urlState.consultationType)
        );
    };

    return (
        <Card alwaysCard={false}>
            <h2 className="mb-6">
                {format(R.HowDoYouWantToContact, caregiverTitle)}
            </h2>

            <div className="text-secondary flex flex-col gap-6">
                {videoProducts.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-lg font-medium">
                            {R.VideoConsultations}
                        </span>

                        <div className="flex flex-col gap-4">
                            {videoProducts.map((product) => {
                                const priceFormatted =
                                    getPriceFormatted(product);

                                return (
                                    <NavigationItem
                                        className={
                                            isSelected(product) ? '' : ''
                                        }
                                        key={product.ConsultationType}
                                        caption={product.Texts.Action}
                                        subCaption={priceFormatted}
                                        icon={
                                            <VideoTypeIcon className="icon-box" />
                                        }
                                        onClick={() => onSelectProduct(product)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                {nonVideoProducts.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-lg font-medium">
                            {R.TextConsultations}
                        </span>

                        <div className="flex flex-col gap-4">
                            {nonVideoProducts.map((product) => {
                                const priceFormatted =
                                    getPriceFormatted(product);

                                return (
                                    <NavigationItem
                                        key={product.ConsultationType}
                                        className={
                                            isSelected(product) ? '' : ''
                                        }
                                        caption={product.Texts.Action}
                                        subCaption={priceFormatted}
                                        icon={
                                            <TextTypeIcon className="icon-box" />
                                        }
                                        onClick={() => onSelectProduct(product)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {shouldShowMissingCoverageInfo && (
                <InsuranceProviderInfoCard
                    insuranceProviderName={insuranceProviderName}
                    insuranceProviderLogoPath={insuranceProviderLogoPath}
                />
            )}
        </Card>
    );
};
